import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Lock } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { useAuth } from "../context/AuthContext";
import { AUTH_TOKENS as TOKENS } from "../constants/authTokens";

export default function NoRoleAssigned() {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const { logout, userName } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = TOKENS.bg;
    return () => {
      document.body.style.background = prev;
    };
  }, []);

  const handleSignOut = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: TOKENS.bg,
        color: TOKENS.ink,
        fontFamily: TOKENS.fontSans,
        WebkitFontSmoothing: "antialiased",
        textRendering: "optimizeLegibility",
        display: "grid",
        placeItems: "center",
        px: 2,
        py: "88px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 460,
            background: TOKENS.bgElev,
            border: `1px solid ${TOKENS.line}`,
            borderRadius: TOKENS.radius,
            padding: { xs: "24px", md: "36px 36px 30px" },
            boxShadow: TOKENS.shadowCard,
            textAlign: "center",
          }}
          role="alert"
          aria-live="polite"
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              mx: "auto",
              mb: "18px",
              background: `color-mix(in oklab, ${TOKENS.warn} 10%, transparent)`,
              color: TOKENS.warn,
              border: `1px solid color-mix(in oklab, ${TOKENS.warn} 35%, ${TOKENS.line})`,
            }}
          >
            <RavenIcon icon={Lock} sx={{ fontSize: 28 }} />
          </Box>

          <Box
            component="div"
            sx={{
              fontFamily: TOKENS.fontMono,
              fontSize: 11,
              letterSpacing: ".18em",
              textTransform: "uppercase",
              color: TOKENS.warn,
              mb: "10px",
            }}
          >
            401 · Access not yet granted
          </Box>

          <Box
            component="h2"
            sx={{
              fontFamily: TOKENS.fontSans,
              fontWeight: 700,
              fontSize: 26,
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              m: "0 0 12px",
              textWrap: "balance",
              color: TOKENS.ink,
              "& em": {
                color: accent,
                fontStyle: "normal",
                fontWeight: 700,
              },
            }}
          >
            You don&rsquo;t have a role assigned<Box component="em">.</Box>
          </Box>

          <Box
            component="p"
            sx={{
              color: TOKENS.ink2,
              fontSize: 14.5,
              lineHeight: 1.6,
              m: "0 0 8px",
            }}
          >
            Please ask your admin to assign you a role to access this app.
          </Box>

          {userName ? (
            <Box
              component="p"
              sx={{
                color: TOKENS.muted,
                fontSize: 13,
                lineHeight: 1.5,
                m: "0 0 24px",
              }}
            >
              Signed in as{" "}
              <Box component="span" sx={{ color: TOKENS.ink2, fontWeight: 600 }}>
                {userName}
              </Box>
            </Box>
          ) : (
            <Box sx={{ height: 16 }} />
          )}

          <Button
            variant="contained"
            color="primary"
            size="medium"
            fullWidth
            onClick={handleSignOut}
          >
            Sign out
          </Button>
        </Box>

        <Box
          sx={{
            width: "100%",
            maxWidth: 460,
            mt: "18px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontFamily: TOKENS.fontMono,
            fontSize: 10.5,
            letterSpacing: ".14em",
            textTransform: "uppercase",
            color: TOKENS.muted,
          }}
        >
          <span>© Raven 2026</span>
        </Box>
      </Box>
    </Box>
  );
}
